import { NodeApi } from 'platejs';
import { isEmpty } from 'remeda';
import z from 'zod';

import { type CaptionElement, Caption } from './Caption';
import { type CreditElement, Credit } from './Credit';
import { ParagraphCodec, UnityParagraph } from './Paragraph';
import { TextElementsCodec } from './Text';
import { CURRENT_VERSION, ElementId, UnityElementId, Version } from '../commons';
import { ELEMENT_TYPES, MIME_TYPES } from '../constants';

export const UnityEmbeddedContent = z.object({
    type: z.literal(ELEMENT_TYPES.EMBEDDED_CONTENT),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    contentAttributes: z.object({
        caption: z
            .object({
                richTextFormat: z.array(UnityParagraph),
            })
            .nullable(),
        captionTitle: z.string().nullable(),
        credit: z.string().nullish(),
        fallBackHeight: z.number().nullish(),
        height: z
            .object({
                mobile: z.number().optional(),
                desktop: z.number().optional(),
            })
            .nullish(),
    }),
    src: z.string().nullable(),
    originalSrc: z.string().nullable(),
    mimetype: z.literal(MIME_TYPES.EMBEDDED_CONTENT).nullable(),
});
export type UnityEmbeddedContent = z.infer<typeof UnityEmbeddedContent>;

export const PlateEmbeddedContent = z.object({
    type: z.literal(ELEMENT_TYPES.EMBEDDED_CONTENT),
    id: ElementId,
    data: z.object({
        templateElement: z.boolean().optional(),
        src: z.string().nullable(),
        originalSrc: z.string().nullable(),
        captionTitle: z.string().nullable(),
        mimetype: z.literal(MIME_TYPES.EMBEDDED_CONTENT),
        fallBackHeight: z.number().nullish(),
        height: z
            .object({
                mobile: z.number().optional(),
                desktop: z.number().optional(),
            })
            .nullish(),
    }),
    children: z.tuple([Caption, Credit]),
});
export type EmbeddedContentElement = z.infer<typeof PlateEmbeddedContent>;

export const EmbeddedContentCodec = z.codec(UnityEmbeddedContent, PlateEmbeddedContent, {
    decode: (value) => {
        const captionItems = value.contentAttributes.caption?.richTextFormat[0]?.items;
        const captionData: CaptionElement['data'] = {
            ...(value.templateElement ? { templateElement: value.templateElement } : {}),
        };
        const caption: CaptionElement = {
            type: ELEMENT_TYPES.CAPTION,
            id:
                value.contentAttributes.caption?.richTextFormat[0]?.elementId ??
                crypto.randomUUID(),
            ...(isEmpty(captionData) ? {} : { data: captionData }),
            children: captionItems ? TextElementsCodec.decode(captionItems) : [{ text: '' }],
        };

        const creditData: CreditElement['data'] = {
            ...(value.templateElement ? { templateElement: value.templateElement } : {}),
        };
        const credit: CreditElement = {
            type: ELEMENT_TYPES.CREDIT,
            ...(isEmpty(creditData) ? {} : { data: creditData }),
            children: [{ text: value.contentAttributes.credit ?? '' }],
        };

        const {
            originalSrc,
            src,
            contentAttributes: { fallBackHeight, height, captionTitle },
            type,
            elementId,
        } = value;

        const id = elementId ?? crypto.randomUUID();

        return {
            type,
            id,
            data: {
                ...(value.templateElement ? { templateElement: value.templateElement } : {}),
                src,
                originalSrc,
                fallBackHeight,
                captionTitle,
                height,
                mimetype: MIME_TYPES.EMBEDDED_CONTENT,
            },
            children: [caption, credit] satisfies [CaptionElement, CreditElement],
        };
    },
    encode: (value) => {
        const caption = ParagraphCodec.encode({
            ...value.children[0],
            type: ELEMENT_TYPES.PARAGRAPH,
        });

        const credit = NodeApi.string(value.children[1]);

        const { templateElement, fallBackHeight, captionTitle, height, src, originalSrc } =
            value.data;

        return {
            type: value.type,
            elementId: value.id,
            version: CURRENT_VERSION,
            templateElement,
            contentAttributes: {
                caption: {
                    richTextFormat: [caption],
                },
                credit,
                fallBackHeight,
                height,
                captionTitle,
            },
            ...(value.data?.templateElement ? { templateElement: value.data.templateElement } : {}),
            mimetype: MIME_TYPES.EMBEDDED_CONTENT,
            src: src ?? null,
            originalSrc,
        };
    },
});
